import { useQuery } from "@tanstack/react-query";
import { useContext } from "react";
import { useSelector } from "react-redux";
import { AlertBannerContext } from "@kivunova/kivufrontendcommon";
import { fetchGraphQL } from "../../Graphql/utils";
import { getCompanyDashboardAnalyticsQuery } from "../../Graphql/querries/dashboardQuery";
import { RootState } from "../../store/store";

export interface CompanyDashboardAnalyticsResponse {
  getCompanyDashboardAnalytics: {
    code: string;
    subscriptionAnalytics?: {
      subscriptionStatus?: string;
      lastPaymentAmount?: number;
      nextBillingAt?: string;
    } | null;
    employeeAnalytics?: {
      totalEmployees: number;
      activeEmployees: number;
      recentLoginsInPeriod: number;
      employeesByStatus?: { key: string; value: number }[];
      employeesByRole?: { key: string; value: number }[];
    } | null;
    storeAnalytics?: {
      totalStores: number;
      activeStores: number;
      storesByStatus?: { key: string; value: number }[];
    } | null;
    supplierAnalytics?: {
      totalSuppliers: number;
      activeSuppliers: number;
      suppliersByStatus?: { key: string; value: number }[];
    } | null;
    customerAnalytics?: {
      totalCustomers: number;
      newCustomersInPeriod: number;
      activeCustomersInPeriod: number;
      repeatCustomers: number;
      averageOrdersPerCustomer: number;
      customersWithOutstandingInvoices: number;
      customersByType?: { key: string; value: number }[];
      totalOutstandingByCustomer?: { key: string; value: number }[];
    } | null;
    productAnalytics?: {
      totalProducts: number;
      activeProducts: number;
      totalProductItems: number;
      inStockProductItems: number;
      totalCategories: number;
      activeCategories: number;
      productsByStatus?: { key: string; value: number }[];
      productItemsByState?: { key: string; value: number }[];
      categoriesByStatus?: { key: string; value: number }[];
    } | null;
    orderAnalytics?: {
      totalOrders: number;
      totalRevenue: number;
      revenueInPeriod: number;
      vatToPay: number;
      totalInvoices: number;
      completedOrdersInPeriod: number;
      orderCountInPeriod: number;
      ordersByStatus?: { key: string; value: number }[];
      invoicesByStatus?: { key: string; value: number }[];
    } | null;
    /** Daily series when input includes dateFrom and dateTo (yyyy-MM-dd). */
    salesInventoryTrend?:
      | {
          date: string;
          sales?: number | null;
          inventory?: number | null;
        }[]
      | null;
  };
}

interface UseCompanyDashboardAnalyticsInput {
  dateFrom?: string | null;
  dateTo?: string | null;
  /** Company admin only: set to scope dashboard to one store; omit or null for all stores. */
  storeId?: string | null;
}

export const useCompanyDashboardAnalytics = (
  input?: UseCompanyDashboardAnalyticsInput | null
) => {
  const { setAlert } = useContext(AlertBannerContext);
  const userDetails = useSelector((state: RootState) => state.user.userDetails);
  const companyId = userDetails?.companyId;

  return useQuery<CompanyDashboardAnalyticsResponse, Error>({
    queryKey: [
      "companyDashboardAnalytics",
      companyId,
      input?.dateFrom,
      input?.dateTo,
      input?.storeId ?? null
    ],
    queryFn: async () => {
      const storeId =
        input?.storeId != null && String(input.storeId).trim() !== ""
          ? String(input.storeId).trim()
          : undefined;
      return fetchGraphQL(
        getCompanyDashboardAnalyticsQuery,
        {
          input: input
            ? {
                dateFrom: input.dateFrom ?? undefined,
                dateTo: input.dateTo ?? undefined,
                ...(storeId ? { storeId } : {})
              }
            : {}
        },
        setAlert
      );
    },
    enabled: !!companyId
  });
};
